import React, {Children, useEffect, useId, useRef, useState} from 'react';

const MIN_SHARE=25,MAX_SHARE=75;
const clamp=value=>Math.min(MAX_SHARE,Math.max(MIN_SHARE,value));
function readShare(key,fallback) {
    try {
        const value=window.localStorage.getItem(key);
        return value!==null && value.trim()!=='' && Number.isFinite(Number(value)) ? clamp(Number(value)) : fallback;
    } catch { return fallback; }
}

export default function PracticeSplitView({children,storageKey,written=false,expanded=false}) {
    const defaultShare=written?40:50;
    const [share,setShare]=useState(()=>readShare(storageKey,defaultShare));
    const [dragging,setDragging]=useState(false);
    const layout=useRef(null),separator=useRef(null),active=useRef(null),latestShare=useRef(share);
    const id=useId();
    const [question,answer]=Children.toArray(children);
    const showShare=value=>{latestShare.current=value;setShare(value);};
    const remember=value=>{try {window.localStorage.setItem(storageKey,String(value));} catch { /* Resizing still works if storage is unavailable. */ }};
    // A layout change or leaving the page must never leave an input-blocking overlay.
    useEffect(()=>()=>active.current?.finish(false,false),[]);
    useEffect(()=>{if(expanded)active.current?.finish(false);},[expanded]);
    const reset=()=>{active.current?.finish(false);showShare(defaultShare);remember(defaultShare);};
    const start=event=>{
        if(event.button!==0 || active.current || expanded || (window.matchMedia && !window.matchMedia('(min-width: 901px)').matches))return;
        const area=layout.current,handle=separator.current;
        const rect=area.getBoundingClientRect(),handleRect=handle.getBoundingClientRect(),style=window.getComputedStyle(area);
        const left=rect.left+(parseFloat(style.paddingLeft)||0);
        const available=rect.width-(parseFloat(style.paddingLeft)||0)-(parseFloat(style.paddingRight)||0)-handleRect.width;
        if(available<=0)return;
        event.preventDefault();handle.focus({preventScroll:true});
        const pointerId=event.pointerId,startShare=latestShare.current,grab=event.clientX-handleRect.left;
        let pending=startShare,frame=null;
        const body=document.body,oldCursor=body.style.cursor,oldSelect=body.style.userSelect;
        const move=next=>{
            if(next.pointerId!==pointerId)return;
            if(next.cancelable)next.preventDefault();
            pending=clamp((next.clientX-left-grab)/available*100);
            // Resize only this layout, at most once per frame. The editors and
            // video keep their existing DOM nodes and are not recreated.
            if(frame===null)frame=window.requestAnimationFrame(()=>{frame=null;showShare(pending);});
        };
        const finish=(commit=true,updateState=true)=>{
            if(!active.current)return;
            active.current=null;
            if(frame!==null)window.cancelAnimationFrame(frame);
            window.removeEventListener('pointermove',move);
            window.removeEventListener('pointerup',up);
            window.removeEventListener('pointercancel',cancel);
            window.removeEventListener('blur',blur);
            window.removeEventListener('resize',cancelResize);
            window.removeEventListener('keydown',key,true);
            body.style.cursor=oldCursor;body.style.userSelect=oldSelect;
            if(updateState){showShare(commit?pending:startShare);setDragging(false);}
            if(commit)remember(pending);
            try {if(handle.hasPointerCapture?.(pointerId))handle.releasePointerCapture(pointerId);} catch { /* Capture may already have ended. */ }
        };
        const up=next=>{if(next.pointerId===pointerId){move(next);finish(true);}};
        const cancel=next=>{if(next.pointerId===pointerId)finish(false);};
        const blur=()=>finish(true),cancelResize=()=>finish(false);
        const key=next=>{if(next.key==='Escape'){next.preventDefault();next.stopPropagation();finish(false);}};
        active.current={finish};
        body.style.cursor='col-resize';body.style.userSelect='none';setDragging(true);
        window.addEventListener('pointermove',move,{passive:false});window.addEventListener('pointerup',up);
        window.addEventListener('pointercancel',cancel);window.addEventListener('blur',blur);window.addEventListener('resize',cancelResize);window.addEventListener('keydown',key,true);
        try {handle.setPointerCapture?.(pointerId);} catch { /* The overlay also protects dragging across iframes. */ }
    };
    const keyboard=event=>{
        const step=event.shiftKey?10:2;
        const change={ArrowLeft:latestShare.current-step,ArrowRight:latestShare.current+step,Home:MIN_SHARE,End:MAX_SHARE,Enter:defaultShare};
        if(!Object.prototype.hasOwnProperty.call(change,event.key))return;
        event.preventDefault();const value=clamp(change[event.key]);showShare(value);remember(value);
    };
    return <div ref={layout} className={`iq-session-body iq-split-layout${written?' iq-session-written':''}${expanded?' iq-session-wide':''}${dragging?' iq-split-dragging':''}`}
        style={{'--iq-question-column':`${share}fr`,'--iq-answer-column':`${100-share}fr`}}>
        <div className="iq-split-controls"><p id={`${id}-help`}>Drag the divider to resize <span>· Question {Math.round(share)}% / Answer {Math.round(100-share)}%</span></p><button type="button" onClick={reset}>Reset split</button></div>
        <div id={`${id}-question`} className="iq-split-pane">{question}</div>
        <div ref={separator} className="iq-split-divider" role="separator" tabIndex={0} aria-label="Resize question and answer panels" aria-orientation="vertical"
            aria-controls={`${id}-question ${id}-answer`} aria-describedby={`${id}-help`} aria-valuemin={MIN_SHARE} aria-valuemax={MAX_SHARE} aria-valuenow={Math.round(share)} aria-valuetext={`Question ${Math.round(share)} percent, answer ${Math.round(100-share)} percent`}
            title="Drag to resize. Arrow keys adjust; Enter or double-click resets." onPointerDown={start} onKeyDown={keyboard} onDoubleClick={reset} onLostPointerCapture={()=>active.current?.finish(false)}>
            <span className="iq-split-grip" aria-hidden="true"><b>↔</b><i/><i/><i/></span>
        </div>
        <div id={`${id}-answer`} className="iq-split-pane">{answer}</div>
        {dragging && <div className="iq-split-shield" aria-hidden="true"/>}
    </div>;
}
